import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Job({ partNumArr, licenseValid, company }) {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("newOrder");
  const [customers, setCustomers] = useState([]);
  const [custNum, setCustNum] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [existingOrderNum, setExistingOrderNum] = useState("");
  const [plant, setPlant] = useState("");
  const [plants, setPlants] = useState([]);
  const [creating, setCreating] = useState(false);
  const [creationResult, setCreationResult] = useState(null);

  const devBaseUrl = process.env.REACT_APP_DEV_URI;
  const selectedPlant = String(plant).split(" - ")[0].trim();

  const getAuthHeaders = () => {
    const token = btoa(
      `${process.env.REACT_APP_USER}:${process.env.REACT_APP_PASSWORD}`,
    );

    return {
      Authorization: `Basic ${token}`,
      "Content-Type": "application/json",
    };
  };

  const getNumberOfPieces = (row) => {
    if (Array.isArray(row)) {
      return Number(row[2]) || 0;
    }

    const value =
      row?.NumberOfPieces ??
      row?.numberOfPieces ??
      row?.["Number of Pieces"] ??
      row?.["NUMBER OF PIECES"] ??
      row?.partLot_Count_c ??
      row?.count;

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  const getLength = (row) => {
    if (Array.isArray(row)) {
      return Number(row[3]) || 0;
    }

    const value =
      row?.Length ??
      row?.length ??
      row?.["Length"] ??
      row?.["LENGTH"] ??
      row?.partLot_Length_c;

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  const getWidth = (row) => {
    if (Array.isArray(row)) {
      return Number(row[4]) || 0;
    }

    const value =
      row?.Width ??
      row?.width ??
      row?.["Width"] ??
      row?.["WIDTH"] ??
      row?.partLot_Width_c;

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  const getHeight = (row) => {
    if (Array.isArray(row)) {
      return Number(row[5]) || 0;
    }

    const value =
      row?.Height ??
      row?.height ??
      row?.Depth ??
      row?.depth ??
      row?.["Height"] ??
      row?.["HEIGHT"] ??
      row?.partLot_Depth_c;

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  const getDynamicValue = (row, aliases, arrayIndex) => {
    if (Array.isArray(row)) {
      return row[arrayIndex];
    }

    return aliases.reduce(
      (value, alias) => value ?? row?.[alias],
      undefined,
    );
  };

  const jobGroups = useMemo(() => {
    const rows = (partNumArr || []).map((row, index) => {
      const partNum = Array.isArray(row)
        ? row[0]
        : row?.partNum || row?.PartNum || row?.partLot_BuildingType_c;

      const elementId = Array.isArray(row)
        ? row[1]
        : row?.partLot_LotNum || row?.LotNum || row?.elementId;

      const count = getNumberOfPieces(row);
      const length = getLength(row);
      const width = getWidth(row);
      const height = getHeight(row);
      const density = getDynamicValue(
        row,
        ["density", "Density", "partLot_Density_c", "partLot_Denisty_c"],
        6,
      );
      const fc = getDynamicValue(
        row,
        ["fc", "fC", "F’C", "F'C", "partLot_FC_c", "partLot_F'C_c"],
        7,
      );
      const type = getDynamicValue(
        row,
        ["type", "Type", "partLot_Type_c"],
        8,
      );

      if (!partNum) {
        return null;
      }

      // Convert dimensions from millimeters to meters for quantity calculation
      const lengthM = length / 1000;
      const widthM = width / 1000;
      const heightM = height / 1000;

      const calculatedQty = lengthM * widthM * heightM * count;

      const group = {
        partNum,
        elementId,

        numberOfPieces: count,
        count,

        length,
        width,
        depth: height,
        height,
        density,
        fc,
        type,

        lengthM,
        widthM,
        heightM,

        calculatedQty,

        qty: calculatedQty,

        elementIds: elementId ? [elementId] : [],
      };

      console.log(`AAC Excel Row ${index + 1}:`, {
        partNum: group.partNum,
        numberOfPieces: group.numberOfPieces,
        length: group.length,
        width: group.width,
        height: group.height,
        calculatedQty: group.calculatedQty,
        elementId: group.elementId,
        density: group.density,
        fc: group.fc,
        type: group.type,
      });

      return group;
    });

    console.log(
      "AAC Job Groups:",
      JSON.stringify(rows.filter(Boolean), null, 2),
    );

    return rows.filter(Boolean);
  }, [partNumArr]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!company) {
        return;
      }

      try {
        const response = await fetch(
          `${devBaseUrl}/v1/BaqSvc/IIT_AAC_Customer(${company})/`,
          { headers: getAuthHeaders() },
        );

        const data = await response.json();

        setCustomers(data.value || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Error fetching customers");
      }
    };

    const fetchPlants = async () => {
      if (!company) {
        return;
      }

      try {
        const response = await axios.get(
          `${devBaseUrl}/v1/Erp.BO.PlantSvc/Plants`,
          { headers: getAuthHeaders() },
        );

        const plantList = response.data.value || [];

        setPlants(plantList);

        if (plantList.length > 0) {
          setPlant(plantList[0].Plant1 || plantList[0].Plant);
        }
      } catch (error) {
        console.error("Error fetching plants:", error);
        toast.error("Error loading sites");
      }
    };

    fetchCustomers();
    fetchPlants();
  }, [company, devBaseUrl]);

  const fetchPartDescription = async (partNum) => {
    try {
      const response = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.PartSvc/Parts?$filter=PartNum eq '${partNum}'`,
        { headers: getAuthHeaders() },
      );

      return response.data.value?.[0]?.PartDescription || partNum;
    } catch {
      return partNum;
    }
  };

  const fetchPartRevision = async (partNum) => {
    const safePartNum = String(partNum).replace(/'/g, "''");

    try {
      const response = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.PartRevSearchSvc/PartRevSearches?$filter=PartNum eq '${safePartNum}'`,
        { headers: getAuthHeaders() },
      );

      const revisions = response.data.value || [];

      if (!revisions.length) {
        console.warn(`No revision found for part ${partNum}`);

        return "";
      }

      const approvedRevision =
        revisions.find((rev) => rev.Approved === true) ||
        revisions.find((rev) => rev.Approved === "true") ||
        revisions[0];

      return approvedRevision.RevisionNum || "";
    } catch (error) {
      console.warn(
        `Revision lookup failed for part ${partNum}. Continuing without revision.`,
        error?.response?.data || error,
      );

      return "";
    }
  };

  const isValidGuid = (value) => {
    if (typeof value !== "string" || !value) {
      return false;
    }

    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value,
    );
  };

  const generateJobNum = (partNum, index) => {
    const cleanPart = String(partNum || "JOB")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);

    const timestamp = Date.now().toString().slice(-8);

    const seq = String(index + 1).padStart(2, "0");

    return `${cleanPart}${timestamp}${seq}`.slice(0, 14);
  };

  const createSalesOrder = async () => {
    const today = new Date().toISOString();

    const requestDate = dueDate ? `${dueDate}T00:00:00` : today;

    const response = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/SalesOrders`,
      {
        Company: company.toUpperCase(),
        CustNum: Number(custNum),
        OpenOrder: true,
        VoidOrder: false,
        OrderHeld: false,
        OrderDate: today,
        RequestDate: requestDate,
        NeedByDate: requestDate,
        RowMod: "A",
      },
      { headers: getAuthHeaders() },
    );

    const orderNum = response.data.OrderNum;

    if (!orderNum) {
      throw new Error("Sales order was created but OrderNum was not returned");
    }

    return orderNum;
  };

  const createOrderLine = async ({ orderNum, group, orderLine }) => {
    const partDescription = await fetchPartDescription(group.partNum);

    const calculatedQty = Number(group.calculatedQty) || 0;

    if (calculatedQty <= 0) {
      throw new Error(
        `Calculated quantity is zero for part ${group.partNum}. Check Length, Width and Height.`,
      );
    }

    console.log("Creating Order Line:", {
      orderNum,
      orderLine,
      partNum: group.partNum,

      OrderQty: calculatedQty,

      NumberOfPieces: group.numberOfPieces,

      Length: group.length,

      Width: group.width,

      Height: group.height,
    });

    const lineResponse = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderDtls`,
      {
        Company: company.toUpperCase(),

        OrderNum: Number(orderNum),

        OrderLine: Number(orderLine),

        PartNum: group.partNum,

        CustNum: Number(custNum),

        OrderQty: calculatedQty.toString(),

        SellingQuantity: calculatedQty.toString(),
      },
      {
        headers: getAuthHeaders(),
      },
    );

    let sysRowID = lineResponse.data.SysRowID || "";

    if (!isValidGuid(sysRowID)) {
      const dtl = await getOrderDtlById({
        orderNum,
        orderLine,
      });

      sysRowID = dtl?.SysRowID || "";
    }

    return {
      orderNum,

      orderLine: lineResponse.data.OrderLine || orderLine,

      sysRowID,

      orderRelNum: 1,

      partNum: group.partNum,

      qty: calculatedQty,

      numberOfPieces: group.numberOfPieces,

      calculatedQty,

      partDescription,

      elementIds: group.elementIds,
    };
  };

  const findAttributeSetID = (value, depth = 0) => {
    if (depth > 12 || value === null || value === undefined) {
      return 0;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findAttributeSetID(item, depth + 1);

        if (found > 0) {
          return found;
        }
      }

      return 0;
    }

    if (typeof value !== "object") {
      return 0;
    }

    const preferredKeys = [
      "AttributeSetID",
      "attributeSetID",
      "InventoryAttributeColumnAttributeSetID",
    ];

    for (const key of preferredKeys) {
      const number = Number(value[key]);

      if (Number.isFinite(number) && number > 0) {
        return number;
      }
    }

    for (const child of Object.values(value)) {
      const found = findAttributeSetID(child, depth + 1);

      if (found > 0) {
        return found;
      }
    }

    return 0;
  };

  const updateOrderLineAttributes = async ({
    orderDtlSysRowID,
    orderRelSysRowID,
    group,
  }) => {
    if (!isValidGuid(orderDtlSysRowID)) {
      throw new Error("Order line SysRowID is missing or invalid");
    }

    if (!isValidGuid(orderRelSysRowID)) {
      throw new Error("Order release SysRowID is missing or invalid");
    }

    /*
     * IMPORTANT:
     *
     * Keep Dynamic Attribute dimensions in the
     * original Excel unit: millimeters.
     *
     * Epicor now calculates TotalValue from these
     * values using the configured calculated expression.
     */
    const length = Number(group.length) || 0;

    const width = Number(group.width) || 0;

    const height = Number(group.height) || 0;
    const density = group.density ?? "";
    const fc = group.fc ?? "";
    const type = group.type ?? "";

    /*
     * This is ONLY used for transaction quantities
     * such as DualQty / OurQty.
     *
     * It is NOT sent as TotalValue.
     */
    const lengthM = length / 1000;

    const widthM = width / 1000;

    const heightM = height / 1000;

    const totalM3 = lengthM * widthM * heightM;

    const numberOfPieces = Number(group.numberOfPieces) || 0;

    if (numberOfPieces < 0) {
      throw new Error(`NumberOfPieces is invalid for part ${group.partNum}`);
    }

    console.log("Sending Advanced UOM:", {
      partNum: group.partNum,

      numberOfPieces,

      LengthMM: length,

      WidthMM: width,

      ThicknessMM: height,

      Density: density,
      "F’C": fc,
      Type: type,

      totalM3,

      orderDtlSysRowID,

      orderRelSysRowID,
    });

    const payload = {
      inventoryDynAttrValueParamsTS: {
        InventoryDynAttrValueSummary: [
          {
            AttrClassID: "Dimensions",

            Company: company.toUpperCase(),

            /*
             * Quantity information remains in m3.
             * TotalValue itself is NOT supplied.
             */
            DualQty: totalM3,

            DualUM: "m3",

            IUM: "m3",

            OurQty: totalM3,

            LineDesc: "",

            PartNum: group.partNum,

            RemainingDualQty: totalM3,

            RemainingOurQty: totalM3,

            RelatedToSchemaName: "Erp",

            RelatedToSysRowID: orderDtlSysRowID,

            RelatedToTableName: "OrderDtl",

            EnteredDualQty: totalM3,

            EnteredOurQty: totalM3,

            UsedInPlanning: false,

            ErrorType: 0,

            ErrorMsg: "",

            NeedToUpdate: true,

            TemplateWhereAvailable: 0,

            ServiceError: false,

            ServiceErrorMessage: "",

            EntityAction1: "",

            EntityAction2: "",

            EntityActionMessage1: "",

            EntityActionMessage2: "",

            EntityActionMessageTitle1: "",

            EntityActionMessageTitle2: "",

            PerformUpdateOnClose: false,

            ShowSummaryInformation: true,

            /*
             * Keep this enabled so Epicor evaluates
             * the calculated TotalValue attribute.
             */
            PerformAttributeOnChangeEvent: true,

            AttributeSetID: 0,

            NumberOfPieces: numberOfPieces,

            CommittedSplitQty: 0,

            RevisionNum: "A",

            TrackInventoryAttributes: false,

            TrackInventoryByRevision: false,

            SysRowID: "00000000-0000-0000-0000-000000000000",

            RowMod: "A",
          },
        ],

        ExtensionTables: [],
      },

      dynAttrValueDS: {
        InventoryDynAttrValues: [
          {
            /*
             * These are the actual Dynamic Attribute
             * input values.
             *
             * Keep them in MILLIMETERS.
             */
            InventoryAttributeColumnNumberOfPieces: numberOfPieces,

            Length: length,

            Width: width,

            Thickness: height,

            Density: density,
            "F’C": fc,
            Type: type,

            /*
             * IMPORTANT:
             *
             * TotalValue is intentionally NOT included.
             *
             * Epicor calculates it from:
             *
             * Length × Width × Thickness
             *
             * according to the new configured
             * calculated attribute expression.
             */

            InventoryAttributeColumnCompany: company.toUpperCase(),

            InventoryAttributeColumnAttrClassID: "Dimensions",

            InventoryAttributeColumnAttributeSetID: 0,

            InventoryAttributeColumnAttributeValueSeq: 0,

            InventoryAttributeColumnPartNum: group.partNum,

            InventoryAttributeColumnTemplateWhereAvailable: 0,

            InventoryAttributeColumnRelatedToSchemaName: "Erp",

            InventoryAttributeColumnRelatedToTableName: "OrderRel",

            InventoryAttributeColumnRelatedToSysRowID: orderRelSysRowID,

            InventoryAttributeColumnSourceRelatedToSchemaName: "Erp",

            InventoryAttributeColumnSourceRelatedToTableName: "OrderDtl",

            InventoryAttributeColumnSourceRelatedToSysRowID: orderDtlSysRowID,

            SysRowID: "00000000-0000-0000-0000-000000000000",

            RowMod: "A",

            hasChanges: true,
          },
        ],
      },
    };

    console.log("Advanced UOM Request:", JSON.stringify(payload, null, 2));

    const response = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.DynAttrValueSetSvc/UpdateAttributeValuesObject`,
      {
        attrClassID: "Dimensions",

        commitLineDtl: false,

        ...payload,

        invTrasferParams: {
          InvTransfer: [],
        },
      },
      {
        headers: getAuthHeaders(),
      },
    );

    console.log(
      "Advanced UOM Response:",
      JSON.stringify(response.data, null, 2),
    );

    const attributeSetID = findAttributeSetID(response.data);

    console.log("Advanced UOM AttributeSetID:", attributeSetID);

    return {
      attributeSetID,

      numberOfPieces,

      /*
       * Return the original dimension values
       * for the rest of the application.
       */
      length,

      width,

      height,

      /*
       * Keep total as m3 for the rest of the
       * order/job quantity flow.
       */
      total: totalM3,
    };
  };

  const patchOrderReleaseMakeToStock = async ({
    orderNum,
    orderLine,
    partNum,
    qty,
  }) => {
    const orderRelNum = 1;

    const response = await axios.patch(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)},${orderRelNum})`,
      {
        Company: company.toUpperCase(),

        OrderNum: Number(orderNum),

        OrderLine: Number(orderLine),

        OrderRelNum: orderRelNum,

        PartNum: partNum,

        Make: false,

        RowMod: "U",
      },
      {
        headers: getAuthHeaders(),
      },
    );

    let sysRowID = response.data?.SysRowID || "";
    let warehouseCode = response.data?.WarehouseCode || "";

    if (!isValidGuid(sysRowID)) {
      const relResponse = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
          orderNum,
        )},${Number(orderLine)},${orderRelNum})`,
        {
          headers: getAuthHeaders(),
        },
      );

      sysRowID = relResponse.data?.SysRowID || "";
      warehouseCode = relResponse.data?.WarehouseCode || warehouseCode;
    }

    return {
      orderRelNum,

      sysRowID,

      warehouseCode,
    };
  };

  const getOrderRelById = async ({ orderNum, orderLine, orderRelNum }) => {
    const response = await axios.get(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)},${Number(orderRelNum)})`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  };

  const patchOrderReleaseAttributeSet = async ({
    orderNum,
    orderLine,
    orderRelNum,
    partNum,
    attributeSetID,
    numberOfPieces,
  }) => {
    const currentRelease = await getOrderRelById({
      orderNum,
      orderLine,
      orderRelNum,
    });

    console.log(
      "Order Release before AUOM update:",
      JSON.stringify(currentRelease, null, 2),
    );

    const response = await axios.patch(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)},${Number(orderRelNum)})`,
      {
        Company: company.toUpperCase(),

        OrderNum: Number(orderNum),

        OrderLine: Number(orderLine),

        OrderRelNum: Number(orderRelNum),

        PartNum: partNum,

        Make: false,

        AttributeSetID: Number(attributeSetID) || 0,

        NumberOfPieces: Number(numberOfPieces) || 0,

        RowMod: "U",
      },
      {
        headers: getAuthHeaders(),
      },
    );

    console.log(
      "Order Release AUOM Patch Response:",
      JSON.stringify(response.data, null, 2),
    );

    const orderRel = await getOrderRelById({
      orderNum,
      orderLine,
      orderRelNum,
    });

    console.log(
      "Order Release after AUOM update:",
      JSON.stringify(orderRel, null, 2),
    );

    return {
      orderRel,

      sysRowID: orderRel?.SysRowID || response.data?.SysRowID || "",

      warehouseCode:
        orderRel?.WarehouseCode || response.data?.WarehouseCode || "",

      attributeSetID: Number(
        orderRel?.AttributeSetID ??
          response.data?.AttributeSetID ??
          attributeSetID ??
          0,
      ),

      numberOfPieces: Number(
        orderRel?.NumberOfPieces ??
          response.data?.NumberOfPieces ??
          numberOfPieces ??
          0,
      ),
    };
  };

  const getOrderDtlById = async ({ orderNum, orderLine }) => {
    const response = await axios.get(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderDtls('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)})`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  };

  const createNewOrderLinks = async () => {
    const orderNum = await createSalesOrder();

    const orderLinks = [];

    for (let i = 0; i < jobGroups.length; i++) {
      const group = jobGroups[i];

      const link = await createOrderLine({
        orderNum,

        group,

        orderLine: i + 1,
      });

      const {
        orderRelNum,

        sysRowID: orderRelSysRowID,

        warehouseCode,
      } = await patchOrderReleaseMakeToStock({
        orderNum,

        orderLine: link.orderLine,

        partNum: link.partNum,

        qty: link.qty,
      });

      const attributeResult = await updateOrderLineAttributes({
        orderDtlSysRowID: link.sysRowID,

        orderRelSysRowID,

        group,
      });

      const savedRelease = await patchOrderReleaseAttributeSet({
        orderNum,

        orderLine: link.orderLine,

        orderRelNum,

        partNum: link.partNum,

        attributeSetID: attributeResult.attributeSetID,

        numberOfPieces: attributeResult.numberOfPieces,
      });

      orderLinks.push({
        ...link,

        orderRelNum,

        orderRelSysRowID: savedRelease.sysRowID,

        warehouseCode: savedRelease.warehouseCode || warehouseCode || "",

        attributeSetID:
          savedRelease.attributeSetID || attributeResult.attributeSetID || 0,

        numberOfPieces:
          savedRelease.numberOfPieces || attributeResult.numberOfPieces,

        length: attributeResult.length,

        width: attributeResult.width,

        height: attributeResult.height,

        total: attributeResult.total,

        calculatedQty: link.calculatedQty,
      });
    }

    return {
      orderNum,

      orderLinks,
    };
  };

  const getLinksFromExistingOrder = async () => {
    const orderNum = Number(existingOrderNum);

    const orderLinks = [];

    for (let i = 0; i < jobGroups.length; i++) {
      const group = jobGroups[i];

      const orderLine = i + 1;

      const existingLine = await getOrderDtlById({
        orderNum,

        orderLine,
      });

      if (!existingLine) {
        throw new Error(
          `Order line ${orderLine} was not found in existing order ${orderNum}`,
        );
      }

      if (
        String(existingLine.PartNum).toUpperCase() !==
        String(group.partNum).toUpperCase()
      ) {
        throw new Error(
          `Order ${orderNum}, line ${orderLine} has part ${existingLine.PartNum}, but uploaded part is ${group.partNum}`,
        );
      }

      const {
        orderRelNum,

        sysRowID: orderRelSysRowID,

        warehouseCode,
      } = await patchOrderReleaseMakeToStock({
        orderNum,

        orderLine,

        partNum: group.partNum,

        qty: group.calculatedQty,
      });

      const attributeResult = await updateOrderLineAttributes({
        orderDtlSysRowID: existingLine.SysRowID || "",

        orderRelSysRowID,

        group,
      });

      const savedRelease = await patchOrderReleaseAttributeSet({
        orderNum,

        orderLine,

        orderRelNum,

        partNum: group.partNum,

        attributeSetID: attributeResult.attributeSetID,

        numberOfPieces: attributeResult.numberOfPieces,
      });

      const partDescription =
        existingLine.LineDesc || (await fetchPartDescription(group.partNum));

      orderLinks.push({
        orderNum,

        orderLine,

        orderRelNum,

        orderRelSysRowID: savedRelease.sysRowID,

        warehouseCode: savedRelease.warehouseCode || warehouseCode || "",

        attributeSetID:
          savedRelease.attributeSetID || attributeResult.attributeSetID || 0,

        partNum: group.partNum,

        qty: group.calculatedQty,

        calculatedQty: group.calculatedQty,

        numberOfPieces:
          savedRelease.numberOfPieces || attributeResult.numberOfPieces,

        length: attributeResult.length,

        width: attributeResult.width,

        height: attributeResult.height,

        total: attributeResult.total,

        partDescription,

        elementIds: group.elementIds,
      });
    }

    return {
      orderNum,

      orderLinks,
    };
  };

  const createJobEntry = async (link, index) => {
    const selectedDueDate = dueDate
      ? `${dueDate}T00:00:00`
      : new Date().toISOString();

    const jobNum = generateJobNum(link.partNum, index);

    const revisionNum = await fetchPartRevision(link.partNum);

    const jobEntryPayload = {
      Company: company,

      JobNum: jobNum,

      PartNum: link.partNum,

      PartDescription: link.partDescription,

      Plant: selectedPlant,

      ReqDueDate: selectedDueDate,

      DueDate: selectedDueDate,

      JobFirm: true,

      JobReleased: false,

      JobHeld: false,

      JobClosed: false,

      JobComplete: false,

      JobEngineered: false,

      JobType: "MFG",

      // Initialize the JobHead from the same values used by the order
      // release and the later Make-to-Stock demand.
      // ProdQty: Number(link.calculatedQty ?? link.qty).toString() || 0,

      AttributeSetID: Number(link.attributeSetID) || 0,

      CommentText: `Created from order ${link.orderNum}, line ${link.orderLine}`,

      OrderNum_c: Number(link.orderNum) || 0,

      OrderLine_c: Number(link.orderLine) || 0,

      OrderRelNum_c: Number(link.orderRelNum) || 0,

      RowMod: "A",
    };

    if (revisionNum) {
      jobEntryPayload.RevisionNum = revisionNum;
    }

    console.log("Creating JobEntry:", JSON.stringify(jobEntryPayload, null, 2));

    const response = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/JobEntries`,
      jobEntryPayload,
      {
        headers: getAuthHeaders(),
      },
    );

    console.log("JobEntry response:", JSON.stringify(response.data, null, 2));

    return {
      jobNum,

      revisionNum,

      sysRowID: response.data?.SysRowID || "",
    };
  };

  const getDetailsForJob = async ({ jobNum, partNum, revisionNum,attributeSetID }) => {
    const sourceFile = revisionNum ? "Method" : "PART";

    const detailsResponse = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/GetDetailsMsgWarning`,
      {
        currJobNum: jobNum,

        currAsmSeq: 0,

        sourceFile: sourceFile,

        sourceQuote: 0,

        sourceLine: 0,

        sourceJob: "",

        sourceAsm: 0,

        sourcePart: partNum,

        sourceRev: revisionNum || "",

        sourceAltMethod: "",

        resequence: false,

        useMethodForParts: false,

        getCostsFromInv: false,

        getCostsFromTemp: false,
      },
      {
        headers: getAuthHeaders(),
      },
    );

    const ds = detailsResponse.data?.returnObj;

    if (ds) {
      await axios.post(
        `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/Update`,
        {
          ds,
        },
        {
          headers: getAuthHeaders(),
        },
      );
    }

    return detailsResponse.data;
  };

  const createMakeToStockDemand = async ({
    jobNum,
    partNum,
    prodQty,
    warehouseCode,
    attributeSetID,
  }) => {
    if (!warehouseCode) {
      throw new Error(
        `Order release did not return WarehouseCode for ${partNum}; cannot create Make-to-Stock demand.`,
      );
    }

    // Keep the same direct JobProd insert used by the original ORDER flow.
    // Stock demands have no order reference; the warehouse identifies the
    // destination for the produced quantity.
    const demandResponse = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/JobProds`,
      {
        Company: company.toUpperCase(),
        JobNum: jobNum,
        MakeToType: "STOCK",
        ProdQty: Number(prodQty).toString(),
        WarehouseCode: warehouseCode,
        MakeToStockQty: Number(prodQty).toString(),
        PartNum: partNum,
        TargetAssemblySeq: 0,
        AttributeSetID: Number(attributeSetID) || 0,
        RowMod: "A",
      },
      { headers: getAuthHeaders() },
    );
    const updateResponse = demandResponse;

    console.log("Make-to-stock demand created:", {
      jobNum,
      partNum,
      quantity: prodQty,
      response: updateResponse.data,
    });

    // Epicor can commit JobProd slightly after the POST response and may
    // return the quantity using a different numeric field/precision. Retry
    // the read and verify the persisted stock demand without requiring an
    // exact floating-point representation.
    const expectedQty = Number(prodQty);
    const quantitiesMatch = (row) => {
      const quantityFields = [
        "MakeToStockQty",
        "ProdQty",
        "Quantity",
        "OurQty",
      ];
      const returnedQuantities = quantityFields
        .map((field) => Number(row?.[field]))
        .filter(Number.isFinite);

      return (
        !returnedQuantities.length ||
        returnedQuantities.some(
          (quantity) => Math.abs(quantity - expectedQty) < 0.000001,
        )
      );
    };

    let savedDemand;
    for (let attempt = 1; attempt <= 3 && !savedDemand; attempt += 1) {
      const verifyResponse = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/GetByID`,
        {
          params: { jobNum },
          headers: getAuthHeaders(),
        },
      );

      const jobProdRows = verifyResponse.data?.returnObj?.JobProd || [];
      console.log(`JobProd verification attempt ${attempt}:`, jobProdRows);

      savedDemand = jobProdRows.find((row) => {
        const samePart =
          String(row.PartNum).toUpperCase() === String(partNum).toUpperCase();
        const isStockDemand =
          row.MakeToType == null ||
          String(row.MakeToType).toUpperCase() === "STOCK";
        const hasNoOrderReference =
          row.OrderNum == null || Number(row.OrderNum) === 0;

        return (
          samePart &&
          isStockDemand &&
          hasNoOrderReference &&
          quantitiesMatch(row)
        );
      });

      if (!savedDemand && attempt < 3) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    if (!savedDemand) {
      throw new Error(
        `Make-to-Stock demand was not persisted for job ${jobNum}`,
      );
    }

    return updateResponse.data;
  };

const updateJobQuantitiesAndOrderReference = async ({
  jobNum,
  partNum,
  prodQty,
  orderNum,
  orderLine,
  orderRelNum,
  attributeSetID,
  numberOfPieces,
}) => {
  const parsedAttributeSetID = Number(attributeSetID);

  if (
    !Number.isInteger(parsedAttributeSetID) ||
    parsedAttributeSetID <= 0
  ) {
    throw new Error(
      `Cannot update job ${jobNum}: invalid AttributeSetID ` +
        `${attributeSetID}. The Advanced UOM attribute set must be retrieved ` +
        `from the related order release before updating the job.`,
    );
  }

  const response = await axios.get(
    `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/GetByID`,
    {
      params: { jobNum },
      headers: getAuthHeaders(),
    },
  );

  const ds = response.data?.returnObj;

  if (!ds) {
    throw new Error(
      `Epicor did not return JobEntry dataset for job ${jobNum}`,
    );
  }

  if (!Array.isArray(ds.JobHead) || !ds.JobHead.length) {
    throw new Error(`JobHead collection was not returned for job ${jobNum}`);
  }

  if (!Array.isArray(ds.JobPart)) {
    throw new Error(`JobPart collection was not returned for job ${jobNum}`);
  }

  const jobHead = ds.JobHead[0];

  jobHead.ProdQty = Number(prodQty);
  jobHead.OrderNum_c = Number(orderNum) || 0;
  jobHead.OrderLine_c = Number(orderLine) || 0;
  jobHead.OrderRelNum_c = Number(orderRelNum) || 0;
  jobHead.RowMod = "U";

  const matchingParts = ds.JobPart.filter(
    (row) =>
      String(row.PartNum).toUpperCase() === String(partNum).toUpperCase(),
  );

  if (!matchingParts.length) {
    throw new Error(`JobPart for ${partNum} was not found on job ${jobNum}`);
  }

  const jobPart =
    matchingParts.find((row) => Number(row.AssemblySeq) === 0) ||
    matchingParts[0];

  const pieces = Number(numberOfPieces) > 0 ? Number(numberOfPieces) : 1;
  const partQtyPerPiece = Number(prodQty) / pieces;

  console.log("JobPart before AUOM update:", JSON.stringify(jobPart, null, 2));

  // calculatedQty already includes all pieces. JobPart.PartQty is the
  // per-piece quantity when NumberOfPieces is populated; Epicor multiplies
  // these two fields when it calculates JobHead.ProdQty.
  jobPart.PartQty = partQtyPerPiece;

  // Supply the Advanced UOM attribute set for this part.
  jobPart.AttributeSetID = parsedAttributeSetID;

  // Populate the piece count if the returned JobPart row supports this field.
  if ("NumberOfPieces" in jobPart) {
    jobPart.NumberOfPieces = Number(numberOfPieces) || 0;
  }

  jobPart.RowMod = "U";

  console.log("JobHead before Update:", JSON.stringify(jobHead, null, 2));
  console.log("JobPart before Update:", JSON.stringify(jobPart, null, 2));

  const updateResponse = await axios.post(
    `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/Update`,
    { ds },
    { headers: getAuthHeaders() },
  );

  console.log(
    "JobEntry Update response:",
    JSON.stringify(updateResponse.data, null, 2),
  );

  const verifyResponse = await axios.get(
    `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/GetByID`,
    {
      params: { jobNum },
      headers: getAuthHeaders(),
    },
  );

  const verifyDS = verifyResponse.data?.returnObj;
  const verifyJobHead = verifyDS?.JobHead?.[0];

  const verifyJobPart = verifyDS?.JobPart?.find(
    (row) =>
      String(row.PartNum).toUpperCase() === String(partNum).toUpperCase() &&
      Number(row.AssemblySeq) === Number(jobPart.AssemblySeq),
  );

  console.log("FINAL JOB DETAILS:", {
    JobNum: jobNum,
    JobHeadProdQty: verifyJobHead?.ProdQty,
    JobPartPartQty: verifyJobPart?.PartQty,
    ExpectedJobPartPartQty: partQtyPerPiece,
    JobPartAttributeSetID: verifyJobPart?.AttributeSetID,
    OrderNum_c: verifyJobHead?.OrderNum_c,
    OrderLine_c: verifyJobHead?.OrderLine_c,
    OrderRelNum_c: verifyJobHead?.OrderRelNum_c,
  });

  if (Number(verifyJobHead?.ProdQty) !== Number(prodQty)) {
    console.warn(`JobHead.ProdQty did not persist as ${prodQty} on job ${jobNum}`);
  }

  if (Number(verifyJobPart?.PartQty) !== Number(partQtyPerPiece)) {
    console.warn(
      `JobPart.PartQty did not persist as ${partQtyPerPiece} on job ${jobNum}`,
    );
  }

  if (Number(verifyJobHead?.OrderNum_c) !== Number(orderNum)) {
    console.warn(`JobHead.OrderNum_c did not persist as ${orderNum} on job ${jobNum}`);
  }

  return verifyDS;
};

const createJobForOrderLine = async (link, index) => {
  const {
    jobNum,
    revisionNum,
    sysRowID,
  } = await createJobEntry(link, index);

  await getDetailsForJob({
    jobNum,
    partNum: link.partNum,
    revisionNum,
  });

  const prodQty = Number(link.calculatedQty ?? link.qty) || 0;

  if (prodQty <= 0) {
    throw new Error(`Job production quantity is zero for job ${jobNum}`);
  }

  console.log("Job AUOM values:", {
    jobNum,
    partNum: link.partNum,
    attributeSetID: link.attributeSetID,
    numberOfPieces: link.numberOfPieces,
  });

  await createMakeToStockDemand({
    jobNum,
    partNum: link.partNum,
    prodQty,
    warehouseCode: link.warehouseCode,
    attributeSetID: link.attributeSetID,
  });

  return {
    jobNum,
    partNum: link.partNum,
    qty: link.qty,
    calculatedQty: link.calculatedQty,
    numberOfPieces: link.numberOfPieces,
    orderNum: link.orderNum,
    orderLine: link.orderLine,
    orderRelNum: link.orderRelNum,
    attributeSetID: link.attributeSetID,
    length: link.length,
    width: link.width,
    height: link.height,
    total: link.total,
    plant: selectedPlant,
    revisionNum,
    sysRowID,
  };
};

  const createOrderAndJobs = async () => {
    if (!company) {
      toast.error("Company is missing");

      return;
    }

    if (!selectedPlant) {
      toast.error("Please select site");

      return;
    }

    if (mode === "newOrder" && !custNum) {
      toast.error("Please select customer");

      return;
    }

    if (mode === "existingOrder" && !existingOrderNum) {
      toast.error("Please enter existing order number");

      return;
    }

    if (!jobGroups.length) {
      toast.error("No uploaded element parts found");

      return;
    }

    const invalidGroup = jobGroups.find(
      (group) => Number(group.calculatedQty) <= 0,
    );

    if (invalidGroup) {
      toast.error(
        `Calculated quantity is zero for part ${invalidGroup.partNum}. Check dimensions.`,
      );

      return;
    }

    setCreating(true);
    setCreationResult(null);

    let createdOrderNum =
      mode === "existingOrder" ? Number(existingOrderNum) : null;
    const createdJobs = [];

    try {
      const {
        orderNum,

        orderLinks,
      } =
        mode === "newOrder"
          ? await createNewOrderLinks()
          : await getLinksFromExistingOrder();

      createdOrderNum = orderNum;
      console.log("Final Order Links:", JSON.stringify(orderLinks, null, 2));

      for (let i = 0; i < orderLinks.length; i++) {
        const createdJob = await createJobForOrderLine(orderLinks[i], i);

        createdJobs.push(createdJob);
      }

      setCreationResult({
        type: "success",
        orderNum,
        jobs: createdJobs,
        message: `Order ${orderNum} and ${createdJobs.length} jobs were created successfully.`,
      });

      console.log("Order:", orderNum);

      console.log("Created Jobs:", JSON.stringify(createdJobs, null, 2));
    } catch (error) {
      console.error("Error creating order/jobs:", error);

      console.error("Epicor error body:", error?.response?.data);

      const errorMessage =
        error?.response?.data?.ErrorMessage ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : JSON.stringify(error?.response?.data)) ||
        error?.message ||
        "Error creating order/jobs";

      setCreationResult({
        type: "error",
        orderNum: createdOrderNum,
        jobs: createdJobs,
        message: errorMessage,
      });

    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="Form-Panel m-1 px-2 py-2">
      <div className="title-border-bottom">
        <button
          className="toggle-btn my-3 mx-5"
          onClick={() => setShowForm(!showForm)}
          style={{
            cursor: licenseValid ? "pointer" : "not-allowed",

            color: licenseValid ? "black" : "gray",
          }}
          disabled={!licenseValid}
        >
          <i
            className="bi bi-arrow-down-short"
            style={{
              fontSize: "2rem",

              fontWeight: "bolder",

              color: licenseValid ? "black" : "gray",
            }}
          ></i>
          Job Details
        </button>
      </div>

      {showForm && (
        <div className="mb-2">
          <form className="p-2 m-2 form-bg border rounded">
            <div className="container">
              <div className="row">
                <div className="col-md-4">
                  <label>Flow</label>

                  <select
                    className="form-control"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <option value="newOrder">Create New Order and Jobs</option>

                    {/* <option value="existingOrder">
                      Create Jobs from Existing Order
                    </option> */}
                  </select>
                </div>

                <div className="col-md-4">
                  <label>Site</label>

                  <select
                    className="form-control"
                    value={plant}
                    onChange={(e) => setPlant(e.target.value)}
                  >
                    <option value="">Select Site</option>

                    {plants.map((p) => (
                      <option
                        key={p.Plant1 || p.Plant}
                        value={p.Plant1 || p.Plant}
                      >
                        {p.Plant1 || p.Plant} {p.Name ? `- ${p.Name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label>Due Date</label>

                  <input
                    type="date"
                    className="form-control"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="row mt-3">
                {mode === "newOrder" && (
                  <div className="col-md-4">
                    <label>Customer</label>

                    <select
                      className="form-control"
                      value={custNum}
                      onChange={(e) => setCustNum(e.target.value)}
                    >
                      <option value="">Select Customer</option>

                      {customers.map((customer) => (
                        <option
                          key={customer.Customer_CustNum}
                          value={customer.Customer_CustNum}
                        >
                          {customer.Customer_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {mode === "existingOrder" && (
                  <div className="col-md-4">
                    <label>Existing Order Number</label>

                    <input
                      type="number"
                      className="form-control"
                      value={existingOrderNum}
                      onChange={(e) => setExistingOrderNum(e.target.value)}
                      placeholder="Order Number"
                    />
                  </div>
                )}
              </div>

              <div className="my-3">
                <strong>Jobs to create:</strong>

                {jobGroups.length === 0 ? (
                  <p>No uploaded element parts found.</p>
                ) : (
                  <ul>
                    {jobGroups.map((group, index) => (
                      <li key={index}>
                        {group.partNum}: {group.numberOfPieces} pcs,{" "}
                        {group.length} x {group.width} x {group.height} mm → Qty{" "}
                        {group.calculatedQty} m³
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="button"
                className="submit-btn ro d-block m-2 rounded"
                onClick={createOrderAndJobs}
                disabled={creating || !licenseValid}
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {creationResult && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="creation-result-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            backgroundColor: "rgba(15, 23, 42, 0.55)",
          }}
        >
          <div
            style={{
              width: "min(680px, 100%)",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: "14px",
              boxShadow: "0 18px 50px rgba(0, 0, 0, 0.25)",
              padding: "1.5rem",
              borderTop: `6px solid ${
                creationResult.type === "success" ? "#198754" : "#dc3545"
              }`,
            }}
          >
            <h4 id="creation-result-title" className="mb-2">
              {creationResult.type === "success"
                ? "Creation completed"
                : "Creation needs attention"}
            </h4>
            {creationResult.orderNum && (
              <p className="mb-2">
                <strong>Order:</strong> {creationResult.orderNum}
              </p>
            )}
            <p className="mb-3">{creationResult.message}</p>

            {creationResult.jobs?.length > 0 && (
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Line</th>
                      <th>Part</th>
                      <th>Job</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creationResult.jobs.map((job, index) => (
                      <tr key={`${job.jobNum || "job"}-${index}`}>
                        <td>{job.orderLine ?? index + 1}</td>
                        <td>{job.partNum}</td>
                        <td>{job.jobNum}</td>
                        <td>{job.calculatedQty ?? job.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-end">
              <button
                type="button"
                className={`btn ${
                  creationResult.type === "success"
                    ? "btn-success"
                    : "btn-danger"
                } px-4`}
                onClick={() => setCreationResult(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default Job;
